/**
 * V4P Product Calculator v5
 * 
 * - Packs mode: show pricing section, quantity input
 * - Units mode: show algorithm with UOM, no pricing section
 */

(function() {
  'use strict';

  if (window.V4PCalculator) {
    console.warn('V4P Calculator: already initialized, skipping duplicate.');
    return;
  }
  
  // ============================================
  // STATE
  // ============================================
  let serviceAvailable = true;
  let fulfilmentMode = 'collection';
  let orderType = 'packs';
  
  let selectedProduct = null;
  let selectedPricing = null;
  let packSizes = [];
  let selectedBaseName = null;
  let algorithmResult = null;
  let selectedProductInStock = null;  // true = in stock, false = out of stock, null = unknown
  let isGroupedProduct = false;       // Whether selected product is a grouped product
  let allPackSizesAreOne = false;     // Whether all pack sizes have size=1
  let manualOverrideActive = false;   // Whether user has customised pack selection
  let manualPackSelections = {};      // { "PK 24": 2, "PK 6": 3 }
  
  // ============================================
  // UTILITIES
  // ============================================
  function formatCurrency(amount) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '\u2013'; // –
  return '\u00A3' + Number(amount).toFixed(2); // £
}

  // Money helper: round to 2dp as a NUMBER (not string)
  function roundMoney(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.round((x + Number.EPSILON) * 100) / 100;
  }

  // Force all pricing fields to 2dp numbers (canonical)
  function normalizePricing(raw) {
    const p = raw || {};
    return {
      // packs
      packSellPriceIncVat: roundMoney(p.packSellPriceIncVat),
      packPurchasePriceIncVat: roundMoney(p.packPurchasePriceIncVat),

      // units
      unitSellPriceIncVat: roundMoney(p.unitSellPriceIncVat),
      unitPurchasePriceIncVat: roundMoney(p.unitPurchasePriceIncVat),
      vatPercent: p.vatPercent ?? 20,  // ✅ ADD THIS
    };
  }

// ============================================
  // LOADING INDICATORS - ANIMAL PARADE
  // ============================================
  
  // All the animals for our veterinary parade!
  const PARADE_ANIMALS = [
    '🐕', '🐈', '🐇', '🐹', '🐴', '🦜', '🐢', '🐠', '🐁', '🦎',
    '🐓', '🦆', '🐑', '🐐', '🐄', '🐖', '🦙', '🐘', '🦒', '🦔',
    '🐿️', '🦡', '🐾', '🐕‍🦺', '🐈‍⬛', '🦮', '🐩', '🐦', '🦉', '🐸'
  ];
  
  /**
   * Get random animals from the parade
   */
  function getRandomAnimals(count = 5) {
    const shuffled = [...PARADE_ANIMALS].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  }
  
  /**
   * Generate loader HTML with cycling animal parade
   * @param {string} message - Loading message to display
   * @param {string} type - 'parade' | 'orbit' | 'search'
   */
  function getLoaderHtml(message = 'Loading...', type = 'parade') {
    const animals = getRandomAnimals(5);
    const orbitAnimals = getRandomAnimals(6);
    const centerAnimal = PARADE_ANIMALS[Math.floor(Math.random() * PARADE_ANIMALS.length)];
    
    switch (type) {
      case 'orbit':
        return `
          <div class="v4p-loader">
            <div class="v4p-loader__orbit">
              ${orbitAnimals.map(a => `<span class="v4p-loader__orbit-animal">${a}</span>`).join('')}
              <span class="v4p-loader__orbit-center">${centerAnimal}</span>
            </div>
            <div class="v4p-loader__text">${escapeHtml(message)}</div>
          </div>
        `;
      
      case 'parade':
      default:
        return `
          <div class="v4p-loader">
            <div class="v4p-loader__parade">
              ${animals.map(a => `<span class="v4p-loader__animal">${a}</span>`).join('')}
            </div>
            <div class="v4p-loader__text">${escapeHtml(message)}</div>
          </div>
        `;
    }
  }
  
  /**
   * Get inline search loader HTML with animal parade
   */
  function getSearchLoaderHtml() {
    const animals = getRandomAnimals(4);
    return `
      <div class="v4p-search-loader">
        <div class="v4p-search-loader__parade">
          ${animals.map(a => `<span class="v4p-search-loader__animal">${a}</span>`).join('')}
        </div>
        <span class="v4p-search-loader__text">Sniffing out products...</span>
      </div>
    `;
  }
  
  /**
   * Get empty state HTML with gentle animals
   */
  function getEmptyStateHtml(message = 'Search for a product above') {
    const animals = getRandomAnimals(3);
    return `
      <div class="v4p-loader v4p-loader--empty">
        <div class="v4p-loader__parade">
          ${animals.map(a => `<span class="v4p-loader__animal">${a}</span>`).join('')}
        </div>
        <div class="v4p-loader__text">${escapeHtml(message)}</div>
      </div>
    `;
  }
  
  /**
   * Start cycling animals in a loader element
   * Returns a function to stop the cycling
   */
  function startAnimalCycling(containerSelector, intervalMs = 800) {
    const container = document.querySelector(containerSelector);
    if (!container) return () => {};
    
    const interval = setInterval(() => {
      const animalEls = container.querySelectorAll('.v4p-loader__animal, .v4p-search-loader__animal, .v4p-loader__orbit-animal');
      if (animalEls.length === 0) return;
      
      // Pick a random animal element to change
      const randomIdx = Math.floor(Math.random() * animalEls.length);
      const newAnimal = PARADE_ANIMALS[Math.floor(Math.random() * PARADE_ANIMALS.length)];
      
      // Animate the swap
      const el = animalEls[randomIdx];
      el.style.transform = 'scale(0)';
      
      setTimeout(() => {
        el.textContent = newAnimal;
        el.style.transform = '';
      }, 150);
      
    }, intervalMs);
    
    return () => clearInterval(interval);
  }
  
  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Show/hide stock server warning message
   */
  function showStockWarning(available, message) {
    const warningEl = document.getElementById('stock-warning');
    if (!warningEl) return;
    
    if (available === false && message) {
      warningEl.innerHTML = `<div class="v4p-stock-warning">${escapeHtml(message)}</div>`;
      warningEl.style.display = '';
    } else {
      warningEl.style.display = 'none';
    }
  }
  
  function formatFieldValue(value) {
    if (value === null || value === undefined || value === '') return '\u2013'; // –
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return String(value);
  }

  // ============================================
// FULFILMENT MODE MIXING CHECK
// ============================================
/**
 * Check if adding an item with current fulfilment mode is allowed.
 * Collection and Delivery items cannot be mixed in the same order.
 * Returns true if allowed, false if blocked (and shows alert).
 */
function checkFulfilmentModeMixingAllowed() {
  // Get current order lines from Order Manager
  const orderLines = window.V4POrderManager?.getOrderLines?.() || [];
  
  // If basket is empty, anything is allowed
  if (orderLines.length === 0) {
    return true;
  }
  
  // Check existing lines' fulfilment modes
  const existingHasCollection = orderLines.some(l => l.fulfilmentMode === 'collection');
  const existingHasDelivery = orderLines.some(l => l.fulfilmentMode === 'delivery');
  
  // Trying to add Collection item to Delivery basket
  if (fulfilmentMode === 'collection' && existingHasDelivery) {
    alert('⚠️ Collection items cannot be mixed with Delivery items in the same order.\n\nPlease start a new order or remove the current items first.');
    return false;
  }
  
  // Trying to add Delivery item to Collection basket
  if (fulfilmentMode === 'delivery' && existingHasCollection) {
    alert('⚠️ Delivery items cannot be mixed with Collection items in the same order.\n\nPlease start a new order or remove the current items first.');
    return false;
  }
  
  return true;
}
  
  // ============================================
  // CD MIXING CHECK
  // ============================================
  /**
   * Check if a product can be added based on CD (Controlled Drug) mixing rules.
   * CD products cannot be mixed with non-CD products in the same order.
   * Returns true if allowed, false if blocked (and shows alert).
   */
  function checkCdMixingAllowed(isControlledDrug) {
    // Get current order lines from Order Manager
    const orderLines = window.V4POrderManager?.getOrderLines?.() || [];
    
    // If basket is empty, anything is allowed
    if (orderLines.length === 0) {
      return true;
    }
    
    const existingHasCD = orderLines.some(l => l.controlledDrug);
    const existingHasNonCD = orderLines.some(l => !l.controlledDrug);
    
    // Trying to add CD to non-CD basket
    if (isControlledDrug && existingHasNonCD) {
      alert('⚠️ Controlled Drug products cannot be mixed with non-Controlled Drug products in the same order.\n\nPlease start a new order or remove the current items first.');
      return false;
    }
    
    // Trying to add non-CD to CD basket
    if (!isControlledDrug && existingHasCD) {
      alert('⚠️ Non-Controlled Drug products cannot be added to an order containing Controlled Drugs.\n\nPlease start a new order or remove the current items first.');
      return false;
    }
    
    return true;
  }
  /**
 * CD DELIVERY RULE
 * If product is Controlled Drug, it can only be ordered on Delivery.
 * If user is in Collection mode, prompt them to switch or block.
 * Also warns about needing a new order if basket currently contains non-CD items.
 */
function ensureCdDeliveryMode(isControlledDrug) {
  if (!isControlledDrug) return true;

  // If already in delivery, fine
  if (fulfilmentMode === 'delivery') return true;

  // Check current basket for non-CD items
  const orderLines = window.V4POrderManager?.getOrderLines?.() || [];
  const existingHasNonCD = orderLines.some(l => !l.controlledDrug);
  const existingHasCD = orderLines.some(l => !!l.controlledDrug);

  // Build message
  let msg =
    '⚠️ Controlled Drug products are only available for home delivery.\n\n' +
    'Please switch Receive → Delivery to continue.';

  if (orderLines.length > 0 && existingHasNonCD) {
    msg +=
      '\n\nNote: your current order contains non-Controlled Drug items. ' +
      'CD items must be placed in a separate order, so you will need to start a new order or remove existing items first.';
  }

  // Confirm switch
  const ok = confirm(msg);
  if (!ok) return false;

  // Switch UI + state to delivery
  setFulfilmentMode('delivery');

  // Delivery is currently disabled — check if switch actually succeeded   // ← NEW
  if (fulfilmentMode !== 'delivery') {                                     // ← NEW
    return false;                                                          // ← NEW
  }                                                                        // ← NEW

  // After switching to delivery, re-check basket mixing
  return true;
}

  // ============================================
  // ROUTE RESOLUTION
  // ============================================
  function getResolvedRoute() {
    if (fulfilmentMode === 'collection') {
      return V4P_CONFIG.routes.collection || 'vtp';
    } else {
      return orderType === 'packs' ? 'vtcp' : 'vtcu';
    }
  }
  
  function updateRouteDisplay() {
    const badge = document.getElementById('route-badge');
    const desc = document.getElementById('route-description');
    const route = getResolvedRoute().toUpperCase();
    
    if (badge) badge.textContent = `Route: ${route}`;
    
    const descriptions = {
      'DTP': 'Direct to Pharmacy \u2013 you collect from supplier',
      'VTP': 'Via V4P \u2013 you order stock, dispense to customer',
      'VTCP': 'V4P ships packs directly to customer',
      'VTCU': 'V4P dispenses & ships units to customer'
    };
    if (desc) desc.textContent = descriptions[route] || '';
  }
  
  // ============================================
  // WORKER HEALTH CHECK
  // ============================================
  async function checkWorkerHealth() {
    try {
      const response = await fetch(`${V4P_CONFIG.workerUrl}/health`, { method: 'GET' });
      const data = await response.json();
      serviceAvailable = data.status === 'ok';
    } catch (e) {
      serviceAvailable = false;
    }
  }
  
  // ============================================
// PRODUCT SEARCH
// ============================================
function initProductSearch() {
  const searchInput = document.getElementById('product-search');
  const resultsContainer = document.getElementById('product-results');
  const clearBtn = document.getElementById('product-search-clear');
  
  if (!searchInput) return;
  
  let timeout = null;
  let lastQuery = '';
  let isMouseOverResults = false;
  
  // Debounced search on input
  searchInput.addEventListener('input', function() {
    const query = this.value.trim();
    clearTimeout(timeout);
    
    // Show/hide clear button
    if (clearBtn) {
      clearBtn.style.display = query.length > 0 ? 'block' : 'none';
    }
    
    if (query.length < 2) {
      hideResults();
      lastQuery = '';
      return;
    }
    
    // Only search if query actually changed
    if (query !== lastQuery) {
      timeout = setTimeout(() => {
        searchProducts(query);
        lastQuery = query;
      }, 300);
    }
  });
  
  // On focus: re-search if we have a query
  searchInput.addEventListener('focus', function() {
    const query = this.value.trim();
    if (query.length >= 2) {
      // Re-search to show results again
      searchProducts(query);
      lastQuery = query;
    }
  });
  
  // Track mouse over results container
  if (resultsContainer) {
    resultsContainer.addEventListener('mouseenter', () => {
      isMouseOverResults = true;
    });
    resultsContainer.addEventListener('mouseleave', () => {
      isMouseOverResults = false;
    });
    
    // Prevent blur from firing before click completes
    resultsContainer.addEventListener('mousedown', (e) => {
      e.preventDefault();
    });
  }
  
  // On blur: only hide if mouse not over results
  searchInput.addEventListener('blur', () => {
    setTimeout(() => {
      if (!isMouseOverResults) {
        hideResults();
      }
    }, 150);
  });
  
  // Clear button handler
  if (clearBtn) {
    clearBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      searchInput.value = '';
      lastQuery = '';
      hideResults();
      clearBtn.style.display = 'none';
      searchInput.focus();
    });
  }
  
  // Keyboard shortcuts
  searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      hideResults();
      searchInput.blur();
    }
  });
  
  function showResults() {
    if (resultsContainer) resultsContainer.classList.add('visible');
  }
  
  function hideResults() {
    if (resultsContainer) resultsContainer.classList.remove('visible');
  }
}

  async function searchProducts(query) {
  const countEl = document.getElementById('results-count');
  const listEl = document.getElementById('results-list');
  const containerEl = document.getElementById('product-results');

  if (!serviceAvailable) {
    if (countEl) countEl.textContent = 'Service unavailable';
    return;
  }

  // Show loading state
  if (countEl) countEl.textContent = 'Searching...';
  if (listEl) listEl.innerHTML = getSearchLoaderHtml();
  if (containerEl) containerEl.classList.add('visible');

  try {
    const response = await fetch(`${V4P_CONFIG.workerUrl}/search-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query,
        mode: orderType,
        limit: 25,
        legalMode: "only" // THIS PAGE
      })
    });

    const data = await response.json();
    const products = data.products || [];
    const resultMode = (data.mode || orderType); // "packs" or "units"

    if (countEl) {
      const extra = data.overflow ? ` (showing top ${products.length} of ${data.totalMatches})` : '';
      countEl.textContent = `${products.length} products found${extra}`;
    }

    if (listEl) listEl.innerHTML = '';

    if (products.length === 0) {
      if (listEl) {
        listEl.innerHTML =
          '<div class="v4p-search-result"><span class="v4p-text-muted">No products found</span></div>';
      }
      if (containerEl) containerEl.classList.add('visible');
      return;
    }

    products.forEach(product => {
      const item = document.createElement('div');
     // Only grey out if definitively out of stock (false), not if unknown (null)
      item.className = 'v4p-search-result' + (product.inStock === false ? ' v4p-search-result--oos' : '');

      let badges = '';

      // --- Legal badge from description ---
      const desc = (product.description || '').toUpperCase();

      let legalBadge = null;
      if (desc.includes('POM-VPS')) legalBadge = 'POM-VPS';
      else if (desc.includes('POM-V')) legalBadge = 'POM-V';
      else if (desc.includes('NFA-VPS')) legalBadge = 'NFA-VPS';

      if (legalBadge) {
        badges += `<span class="v4p-tag v4p-tag--warning v4p-tag--small">${legalBadge}</span> `;
      }

      // --- Other badges ---
      if (product.controlledDrug) badges += '<span class="v4p-tag v4p-tag--danger v4p-tag--small">CD</span> ';
      if (product.coldChain) badges += '<span class="v4p-tag v4p-tag--info v4p-tag--small">Cold</span> ';

      if (product.inStock === true) {
        badges += '<span class="v4p-tag v4p-tag--success v4p-tag--small">In Stock</span> ';
      } else if (product.inStock === false) {
        badges += '<span class="v4p-tag v4p-tag--muted v4p-tag--small">Out of Stock</span> ';
      } else {
        // null = stock server unavailable
        badges += '<span class="v4p-tag v4p-tag--warning v4p-tag--small">Stock TBC</span> ';
      }

      item.innerHTML = `
        <div class="v4p-search-result__name">${escapeHtml(product.displayName)}</div>
        <div class="v4p-search-result__meta">${badges}</div>
      `;

      item.addEventListener('click', () => {
        if (!ensureCdDeliveryMode(product.controlledDrug)) return;
        if (!checkCdMixingAllowed(product.controlledDrug)) return;
        if (!checkFulfilmentModeMixingAllowed()) return;

        if (resultMode === 'packs') {
          if (!product.productId) return;
          selectProduct(product.productId, product.displayName, product.inStock);
        } else {
          // Units mode: check if grouped or singular
          if (product.isGrouped) {
            // Grouped product - use baseName to fetch all pack sizes
            selectBaseName(product.baseName, product.displayName, product.inStock, true);
          } else {
            // Singular/non-grouped product - use productId like packs mode
            if (!product.productId) return;
            selectSingularUnit(product.productId, product.displayName, product.inStock, product.size);
          }
        }
      });

      if (listEl) listEl.appendChild(item);
    });

    // Show stock server warning if applicable
    const stockWarningEl = document.getElementById('stock-warning');
    if (stockWarningEl) {
      if (data.stockCheckAvailable === false && data.stockCheckMessage) {
        stockWarningEl.innerHTML = `<div class="v4p-stock-warning">${escapeHtml(data.stockCheckMessage)}</div>`;
        stockWarningEl.style.display = '';
      } else {
        stockWarningEl.style.display = 'none';
      }
    }

    if (containerEl) containerEl.classList.add('visible');

  } catch (e) {
    console.error('Search error:', e);
    if (countEl) countEl.textContent = 'Search error';
  }
}

  
  // ============================================
  // SELECT PRODUCT (Packs Mode)
  // ============================================
  async function selectProduct(productId, displayName, inStock = true) {
    const searchInput = document.getElementById('product-search');
    const resultsContainer = document.getElementById('product-results');
    const loadingEl = document.getElementById('product-loading');
    const contentEl = document.getElementById('product-content');
    
    if (searchInput) searchInput.value = displayName;
    if (resultsContainer) resultsContainer.classList.remove('visible');
    if (loadingEl) {
      loadingEl.style.display = 'block';
      loadingEl.innerHTML = getLoaderHtml('Fetching product details...', 'parade');
    }
    if (contentEl) contentEl.style.display = 'none';
    
    selectedProduct = null;
    selectedPricing = null;
    packSizes = [];
    selectedBaseName = null;
    selectedProductInStock = inStock;  // Store stock status
    
    try {
      const response = await fetch(`${V4P_CONFIG.workerUrl}/get-product-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: String(V4P_CONFIG.customerId),
          productId: String(productId),
          route: getResolvedRoute()
        })
      });
      
      if (!response.ok) throw new Error('Failed to load product');
      
      const data = await response.json();
      
      selectedProduct = data.product;
      selectedPricing = normalizePricing(data.pricing);

      // Show stock warning if server unavailable
      showStockWarning(data.stockCheckAvailable, data.stockCheckMessage);
      
      // Update in-stock status from response (may be null if server down)
      if (data.inStock !== undefined) {
        selectedProductInStock = data.inStock;
      }
      
      updateProductDisplay();
      updatePricingDisplay();
      updateProductInfoDisplay();
      updateModeDisplay();
      updateAddButtonState();  // ADD THIS
      
      if (loadingEl) loadingEl.style.display = 'none';
      if (contentEl) contentEl.style.display = 'block';
      
    } catch (e) {
      console.error('Error loading product:', e);
      if (loadingEl) loadingEl.innerHTML = '<p class="v4p-text-muted" style="color: #dc3545;">Failed to load product</p>';
    }
  }
  
  // ============================================
  // SELECT BASE NAME (Units Mode)
  // ============================================
  async function selectBaseName(baseName, displayName, inStock = true, grouped = true) {
    const searchInput = document.getElementById('product-search');
    const resultsContainer = document.getElementById('product-results');
    const loadingEl = document.getElementById('product-loading');
    const contentEl = document.getElementById('product-content');
    
    if (searchInput) searchInput.value = displayName;
    if (resultsContainer) resultsContainer.classList.remove('visible');
    if (loadingEl) {
      loadingEl.style.display = 'block';
      loadingEl.innerHTML = getLoaderHtml('Fetching pack sizes...', 'parade');
    }
    if (contentEl) contentEl.style.display = 'none';
    
    selectedProduct = null;
    selectedPricing = null;
    packSizes = [];
    selectedBaseName = baseName;
    selectedProductInStock = inStock;
    isGroupedProduct = grouped;
    allPackSizesAreOne = false;  // Will be set after fetching pack sizes
    
    try {
      const response = await fetch(`${V4P_CONFIG.workerUrl}/get-pack-sizes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: String(V4P_CONFIG.customerId),
          baseName: baseName,
          route: getResolvedRoute()
        })
      });
      
      if (!response.ok) throw new Error('Failed to load pack sizes');
      
      const data = await response.json();
      
      selectedProduct = data.product;
      packSizes = data.packSizes || [];

      // Normalize pricing on every pack size so algorithm math is currency-safe
      packSizes = (packSizes || []).map(ps => ({
        ...ps,
        pricing: normalizePricing(ps.pricing)
      }));
      
      // Use first pack's pricing for unit price calculation
      if (packSizes.length > 0) {
        selectedPricing = packSizes[0].pricing; // already normalized above
      }

      // Show stock warning if server unavailable
      showStockWarning(data.stockCheckAvailable, data.stockCheckMessage);
      
      // Update stock status - anyInStock can be null if server down
      if (data.anyInStock !== undefined) {
        selectedProductInStock = data.anyInStock;
      }
      
      // Check if all pack sizes have size=1 (non-splittable)
      allPackSizesAreOne = packSizes.length > 0 && packSizes.every(ps => ps.size === 1);
      
      updateProductDisplay();
      updateProductInfoDisplay();
      updateModeDisplay();
      updateUnitsInputLabel();
      populateOverrideInputs();  // NEW: Setup manual override grid
      updateAddButtonState();
      
      if (loadingEl) loadingEl.style.display = 'none';
      if (contentEl) contentEl.style.display = 'block';
      
    } catch (e) {
      console.error('Error loading pack sizes:', e);
      if (loadingEl) loadingEl.innerHTML = '<p class="v4p-text-muted" style="color: #dc3545;">Failed to load product</p>';
    }
  }

  // ============================================
  // SELECT SINGULAR UNIT (Units Mode - Non-Grouped)
  // ============================================
  async function selectSingularUnit(productId, displayName, inStock = true, size = 1) {
    const searchInput = document.getElementById('product-search');
    const resultsContainer = document.getElementById('product-results');
    const loadingEl = document.getElementById('product-loading');
    const contentEl = document.getElementById('product-content');
    
    if (searchInput) searchInput.value = displayName;
    if (resultsContainer) resultsContainer.classList.remove('visible');
    if (loadingEl) {
      loadingEl.style.display = 'block';
      loadingEl.innerHTML = getLoaderHtml('Fetching product details...', 'parade');
    }
    if (contentEl) contentEl.style.display = 'none';
    
    selectedProduct = null;
    selectedPricing = null;
    packSizes = [];
    selectedBaseName = null;
    selectedProductInStock = inStock;
    isGroupedProduct = false;
    allPackSizesAreOne = (size === 1);
    
    try {
      const response = await fetch(`${V4P_CONFIG.workerUrl}/get-product-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: String(V4P_CONFIG.customerId),
          productId: String(productId),
          route: getResolvedRoute()
        })
      });
      
      if (!response.ok) throw new Error('Failed to load product');
      
      const data = await response.json();
      
      selectedProduct = data.product;
      selectedPricing = normalizePricing(data.pricing);
      
      // Create a single-item packSizes array for algorithm compatibility
      const productSize = selectedProduct.v4uSize || selectedProduct.size || 1;
      allPackSizesAreOne = (productSize === 1);
      
      packSizes = [{
        productId: selectedProduct.productId,
        variantId: selectedProduct.variantId || null,
        productName: selectedProduct.product,
        uom: selectedProduct.uom,
        size: productSize,
        pricing: selectedPricing,
        inStock: inStock
      }];
      
      // Store baseName for display purposes
      selectedBaseName = selectedProduct.product;

      // Show stock warning if server unavailable
      showStockWarning(data.stockCheckAvailable, data.stockCheckMessage);
      
      // Update in-stock status from response (may be null if server down)
      if (data.inStock !== undefined) {
        selectedProductInStock = data.inStock;
        // Also update the pack's inStock status
        packSizes[0].inStock = data.inStock;
      }
      
      updateProductDisplay();
      updatePricingDisplay();
      updateProductInfoDisplay();
      updateModeDisplay();
      updateUnitsInputLabel();
      updateAddButtonState();
      
      if (loadingEl) loadingEl.style.display = 'none';
      if (contentEl) contentEl.style.display = 'block';
      
    } catch (e) {
      console.error('Error loading product:', e);
      if (loadingEl) loadingEl.innerHTML = '<p class="v4p-text-muted" style="color: #dc3545;">Failed to load product</p>';
    }
  }
  
  // ============================================
  // DISPLAY UPDATES
  // ============================================

    /**
   * Update the Add to Order button based on stock status
   */
  function updateAddButtonState() {
    const addBtn = document.getElementById('btn-add-line');
    if (!addBtn) return;
    
    // Check stock first
    // Only block if definitively out of stock (false), allow if unknown (null)
    if (selectedProductInStock === false) {
      addBtn.style.display = '';
      addBtn.disabled = true;
      addBtn.textContent = 'Out of Stock';
      return;
    }
    
    // Check custom selection validation (only in units mode with manual override)
    if (orderType === 'units' && manualOverrideActive && Object.keys(manualPackSelections).length > 0) {
      const unitsInput = document.getElementById('calc-units-input');
      const unitsNeeded = parseInt(unitsInput?.value || '0', 10);
      
      let totalCustomUnits = 0;
      for (const [uom, qty] of Object.entries(manualPackSelections)) {
        const pack = packSizes.find(p => p.uom === uom);
        if (pack) totalCustomUnits += qty * pack.size;
      }
      
      if (totalCustomUnits < unitsNeeded && unitsNeeded > 0) {
        const shortage = unitsNeeded - totalCustomUnits;
        addBtn.style.display = '';
        addBtn.disabled = true;
        addBtn.textContent = `Need ${shortage} more units`;
        return;
      }
    }
    
    // Default: enabled
    addBtn.style.display = '';
    addBtn.disabled = false;
    addBtn.textContent = 'Add to Order';
  }

  function updateProductDisplay() {
    if (!selectedProduct) return;
    
    const nameEl = document.getElementById('product-name');
    const badgesEl = document.getElementById('product-badges');
    const packSizeEl = document.getElementById('display-pack-size');
    const uomEl = document.getElementById('display-uom');
    
    if (nameEl) nameEl.textContent = selectedProduct.product || '\u2013';

    if (packSizeEl) packSizeEl.textContent = selectedProduct.v4uSize || selectedProduct.size || '\u2013';
    if (uomEl) uomEl.textContent = selectedProduct.uom || '\u2013';
    
    if (badgesEl) {
      let badges = '';

      // --- Legal badge from description ---
      const desc = (selectedProduct.description || '').toUpperCase();
      let legalBadge = null;

      if (desc.includes('POM-VPS')) legalBadge = 'POM-VPS';
      else if (desc.includes('POM-V')) legalBadge = 'POM-V';
      else if (desc.includes('NFA-VPS')) legalBadge = 'NFA-VPS';

      if (legalBadge) {
        badges += `<span class="v4p-tag v4p-tag--warning v4p-tag--small">${legalBadge}</span> `;
      }

      // --- Other badges ---
      if (selectedProduct.controlledDrug) badges += '<span class="v4p-tag v4p-tag--danger v4p-tag--small">CD</span> ';
      if (selectedProduct.coldChain) badges += '<span class="v4p-tag v4p-tag--info v4p-tag--small">❄️</span> ';

      badgesEl.innerHTML = badges;
    }
  }

    function getCanonicalUnitPrices() {
    if (!selectedPricing) return null;

    const route = getResolvedRoute().toLowerCase();
    const isDeliveryRoute = (route === 'vtcp' || route === 'vtcu');

    const customer = {
      pack: selectedPricing.packSellPriceIncVat || 0,
      unit: selectedPricing.unitSellPriceIncVat || 0
    };

    const pharmacy = isDeliveryRoute
      ? { ...customer } // pass-through
      : {
          pack: selectedPricing.packPurchasePriceIncVat || 0,
          unit: selectedPricing.unitPurchasePriceIncVat || 0
        };

    return { customer, pharmacy };
  }

  
  function updatePricingDisplay() {
    if (!selectedProduct || !selectedPricing) return;
    
    const custPackEl = document.getElementById('pricing-customer-pack');
    const custUnitEl = document.getElementById('pricing-customer-unit');
    const pharmPackEl = document.getElementById('pricing-pharmacy-pack');
    const pharmUnitEl = document.getElementById('pricing-pharmacy-unit');
    
    // Check if delivery route (VTCP) - pharmacy cost = customer cost (pass-through)
    const route = getResolvedRoute().toLowerCase();
    const isDeliveryRoute = (route === 'vtcp' || route === 'vtcu');
    
    if (custPackEl) custPackEl.textContent = formatCurrency(selectedPricing.packSellPriceIncVat);
    if (custUnitEl) custUnitEl.textContent = formatCurrency(selectedPricing.unitSellPriceIncVat);
    
    // For delivery routes, pharmacy pays same as customer
    if (isDeliveryRoute) {
      if (pharmPackEl) pharmPackEl.textContent = formatCurrency(selectedPricing.packSellPriceIncVat);
      if (pharmUnitEl) pharmUnitEl.textContent = formatCurrency(selectedPricing.unitSellPriceIncVat);
    } else {
      if (pharmPackEl) pharmPackEl.textContent = formatCurrency(selectedPricing.packPurchasePriceIncVat);
      if (pharmUnitEl) pharmUnitEl.textContent = formatCurrency(selectedPricing.unitPurchasePriceIncVat);
    }
  }
  
  function updateProductInfoDisplay() {
    if (!selectedProduct) return;
    
    const container = document.getElementById('product-info-content');
    if (!container) return;
    
    // Ensure the doc viewer container exists in the modal
    ensureDocViewerExists();
    
    // Widen modal for two-column layout (inject once)
    if (!document.getElementById('v4p-modal-wide-style')) {
      const style = document.createElement('style');
      style.id = 'v4p-modal-wide-style';
      style.textContent = `
        #product-info-modal .v4p-modal__inner {
          max-width: 720px !important;
          width: 95vw !important;
        }
      `;
      document.head.appendChild(style);
    }
    
    const fields = [
      { label: 'Product', key: 'product' },
      { label: 'Description', key: 'description' },
      { label: 'Sales Group', key: 'salesGroup' },
      { label: 'Sales Sub Group', key: 'salesSubGroup' },
      { label: 'Product Family', key: 'productFamily' },
      { label: 'Manufacturer', key: 'manufacturer' },
      { label: 'Manufacturer Code', key: 'manufacturerCode' },
      { label: 'UOM', key: 'uom' },
      { label: 'POM', key: 'pom' },
      { label: 'Controlled Drug', key: 'controlledDrug' },
      { label: 'Cold Chain', key: 'coldChain' },
      { label: 'VMD Product No', key: 'vmdProductNo' },
      { label: 'MA Holder', key: 'maHolder' },
      { label: 'Distributors', key: 'distributors' },
      { label: 'VM No', key: 'vmNo' },
      { label: 'Date of Issue', key: 'dateOfIssue' },
      { label: 'Authorisation Route', key: 'authorisationRoute' },
      { label: 'Territory', key: 'territory' },
      { label: 'Active Substances', key: 'activeSubstances' },
      { label: 'Target Species', key: 'targetSpecies' },
      { label: 'Distribution Category', key: 'distributionCategory' },
      { label: 'Pharmaceutical Form', key: 'pharmaceuticalForm' },
      { label: 'Therapeutic Group', key: 'therapeuticGroup' },
      { label: 'SPC Link', key: 'spcLink', isDocLink: true },
      { label: 'UKPAR Link', key: 'ukparLink', isDocLink: true },
      { label: 'PAAR Link', key: 'paarLink', isDocLink: true }
    ];
    
    // Build field rows
    let fieldsHtml = '';
    fields.forEach(field => {
      const value = selectedProduct[field.key];
      
      // Skip blank/null/undefined fields
      if (value === null || value === undefined || value === '' || value === false) return;
      
      let displayValue;
      if (field.isDocLink && value) {
        displayValue = `<a href="#" class="v4p-doc-link" data-doc-url="${escapeHtml(value)}" data-doc-label="${escapeHtml(field.label)}" style="color: #2563eb; text-decoration: underline; cursor: pointer;">View ${escapeHtml(field.label.replace(' Link', ''))}</a>`;
      } else {
        displayValue = formatFieldValue(value);
      }
      
      fieldsHtml += `
        <div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid rgba(var(--color-foreground), 0.08); font-size: 13px;">
          <span style="color: rgba(var(--color-foreground), 0.6);">${field.label}</span>
          <span style="font-weight: 500; text-align: right; max-width: 60%; word-break: break-word;">${displayValue}</span>
        </div>
      `;
    });
    
    // Two-column layout: image left, fields right
    const imgUrl = selectedProduct.productId
      ? `${V4P_CONFIG.workerUrl}/product-image/${selectedProduct.productId}`
      : null;
    
    let html = '';
    if (imgUrl) {
      html = `
        <div style="display: flex; gap: 20px; min-height: 300px;" id="v4p-info-layout">
          <div id="v4p-info-image-col" style="flex: 0 0 200px; display: flex; align-items: stretch; justify-content: center; background: rgba(var(--color-foreground), 0.02); border-radius: 8px; border: 1px solid rgba(var(--color-foreground), 0.06); padding: 16px;">
            <img src="${imgUrl}"
                 alt="${escapeHtml(selectedProduct.product || 'Product image')}"
                 onerror="document.getElementById('v4p-info-image-col').style.display='none'; document.getElementById('v4p-info-fields-col').style.flex='1 1 100%';"
                 style="width: 100%; height: 100%; object-fit: contain;" />
          </div>
          <div id="v4p-info-fields-col" style="flex: 1 1 0; overflow-y: auto; max-height: 70vh;">
            ${fieldsHtml}
          </div>
        </div>
      `;
    } else {
      html = `<div style="overflow-y: auto; max-height: 70vh;">${fieldsHtml}</div>`;
    }
    
    container.innerHTML = html;
    
    // Bind click handlers for doc links
    container.querySelectorAll('.v4p-doc-link').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const url = link.dataset.docUrl;
        const label = link.dataset.docLabel;
        if (url) openDocInModal(url, label);
      });
    });
  }

  /**
   * Ensure the document viewer container exists inside the modal
   */
  function ensureDocViewerExists() {
    const modal = document.getElementById('product-info-modal');
    if (!modal || document.getElementById('product-doc-viewer')) return;
    
    const viewer = document.createElement('div');
    viewer.id = 'product-doc-viewer';
    viewer.style.display = 'none';
    viewer.style.position = 'absolute';
    viewer.style.inset = '0';
    viewer.style.zIndex = '10';
    viewer.style.background = '#fff';
    viewer.style.flexDirection = 'column';
    
    // Find the modal's inner content wrapper (the panel that has the content)
    const modalInner = modal.querySelector('.v4p-modal__inner') 
                    || modal.querySelector('[class*="modal"]') 
                    || modal.firstElementChild 
                    || modal;
    
    // Make the parent position:relative so absolute positioning works
    if (modalInner !== modal) {
      modalInner.style.position = 'relative';
    }
    
    modalInner.appendChild(viewer);
  }

  /**
   * Open a document (PDF) inside the product info modal
   */
  function openDocInModal(url, label) {
    const infoContent = document.getElementById('product-info-content');
    if (!infoContent) return;
    
    // Stash current product info HTML so we can restore it
    infoContent._savedHtml = infoContent.innerHTML;
    
    // Also hide the modal header if present
    const modalHeading = document.querySelector('#product-info-modal .v4p-modal__header');
    if (modalHeading) modalHeading.style.display = 'none';
    
    const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;
    
    infoContent.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 70vh;">
        <div style="display: flex; align-items: center; gap: 8px; padding: 10px 0; border-bottom: 1px solid #e5e7eb; flex-shrink: 0;">
          <button id="btn-doc-back" style="
            display: inline-flex; align-items: center; gap: 4px;
            padding: 6px 12px; border: 1px solid #d1d5db; border-radius: 6px;
            background: #fff; cursor: pointer; font-size: 13px; font-weight: 500;
            color: #374151;
          ">← Back to Product Info</button>
          <span style="font-size: 13px; font-weight: 600; color: #374151;">${escapeHtml(label || 'Document')}</span>
          <a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" style="
            margin-left: auto; font-size: 12px; color: #2563eb; text-decoration: underline;
          ">Open in new tab ↗</a>
        </div>
        <iframe src="${escapeHtml(viewerUrl)}" style="
          flex: 1; width: 100%; border: none; border-radius: 4px; margin-top: 8px;
        " title="${escapeHtml(label || 'Document')}"></iframe>
      </div>
    `;
    
    document.getElementById('btn-doc-back').addEventListener('click', closeDocViewer);
  }

  /**
   * Close the document viewer and return to product info
   */
  function closeDocViewer() {
    const infoContent = document.getElementById('product-info-content');
    if (!infoContent || !infoContent._savedHtml) return;
    
    infoContent.innerHTML = infoContent._savedHtml;
    delete infoContent._savedHtml;
    
    // Restore modal header
    const modalHeading = document.querySelector('#product-info-modal .v4p-modal__header');
    if (modalHeading) modalHeading.style.display = '';
    
    // Re-bind doc link click handlers
    infoContent.querySelectorAll('.v4p-doc-link').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const url = link.dataset.docUrl;
        const lbl = link.dataset.docLabel;
        if (url) openDocInModal(url, lbl);
      });
    });
  }
  
  /**
   * Update which sections are visible based on mode
   */
  function updateModeDisplay() {
    const pricingSection = document.getElementById('pricing-section'); // packs pricing box
    const unitsSection = document.getElementById('units-section');     // units box
    const modeLabel = document.getElementById('calc-mode-label');

    const modeText = fulfilmentMode === 'collection' ? 'Collection' : 'Delivery';
    const typeText = orderType === 'packs' ? 'Packs' : 'Units';
    if (modeLabel) modeLabel.textContent = `${modeText} – ${typeText}`;

    if (orderType === 'packs') {
      if (pricingSection) pricingSection.style.display = '';
      if (unitsSection) unitsSection.style.display = 'none';
      updatePricingDisplay();
    } else {
      if (pricingSection) pricingSection.style.display = 'none';
      if (unitsSection) unitsSection.style.display = '';
    }

    updateTotals();
  }

/**
   * Update the units input label based on whether packs are splittable
   */
  function updateUnitsInputLabel() {
    const labelEl = document.getElementById('units-input-label');
    if (!labelEl) return;
    
    if (orderType === 'packs') {
      labelEl.textContent = 'Packs Required';
      return;
    }
    
    // Units mode: check if all sizes are 1
    if (allPackSizesAreOne) {
      labelEl.textContent = 'Packs Required';
    } else {
      labelEl.textContent = 'Units Required';
    }
  }
  
// ============================================
// CALCULATOR & ALGORITHM
// ============================================
function initCalculator() {
  const packsInput = document.getElementById('calc-packs-input');
  const unitsInput = document.getElementById('calc-units-input');
  
  // Packs input handlers
  if (packsInput) {
    packsInput.addEventListener('input', function() {
      updateTotals();
      dispatchQuantityChanged();
    });
    packsInput.addEventListener('change', function() {
      updateTotals();
      dispatchQuantityChanged();
    });
  }
  
  // Units input handlers
  if (unitsInput) {
    unitsInput.addEventListener('input', function() {
      updateTotals();
      dispatchQuantityChanged();
    });
    unitsInput.addEventListener('change', function() {
      updateTotals();
      dispatchQuantityChanged();
    });
  }
  
  // Listen for repeat qty changes and sync TO calculator (works for both packs and units)
  window.addEventListener('v4p:repeat-qty-changed', function(e) {
    const qty = e.detail?.quantity;
    if (qty === null || qty === undefined || isNaN(Number(qty))) return;
    
    const newVal = parseInt(qty, 10) || 0;
    
    // Sync to whichever mode is active
    if (orderType === 'units' && unitsInput) {
      const currentVal = parseInt(unitsInput.value || '0', 10);
      if (currentVal !== newVal) {
        unitsInput.value = String(newVal);
        updateTotals();
        // Trigger the input event to run full pricing chain
        unitsInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } else if (orderType === 'packs' && packsInput) {
      const currentVal = parseInt(packsInput.value || '0', 10);
      if (currentVal !== newVal) {
        packsInput.value = String(newVal);
        updateTotals();
        // Trigger the input event to run full pricing chain
        packsInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  });
}

/**
 * Dispatch quantity changed event to sync with repeat fields (works for both packs and units)
 */
function dispatchQuantityChanged() {
  const unitsInput = document.getElementById('calc-units-input');
  const packsInput = document.getElementById('calc-packs-input');
  
  let quantity = 0;
  if (orderType === 'units' && unitsInput) {
    quantity = parseInt(unitsInput.value || '0', 10);
  } else if (orderType === 'packs' && packsInput) {
    quantity = parseInt(packsInput.value || '0', 10);
  }
  
  window.dispatchEvent(new CustomEvent('v4p:quantity-changed', {
    detail: { 
      quantity: quantity,
      orderType: orderType
    }
  }));
}

function updateTotals() {
  const packsInput = document.getElementById('calc-packs-input');
  const unitsInput = document.getElementById('calc-units-input');
  const customerTotalEl = document.getElementById('calc-customer-total');
  const pharmacyTotalEl = document.getElementById('calc-pharmacy-total');
  
  if (!selectedPricing && orderType === 'packs') return;
  if (packSizes.length === 0 && orderType === 'units') return;
  
  let customerTotal = 0;
  let pharmacyTotal = 0;
  
  if (orderType === 'packs') {
    const packs = parseInt(packsInput?.value || '0', 10);

    const route = getResolvedRoute().toLowerCase();
    const isDeliveryRoute = (route === 'vtcp' || route === 'vtcu');

    const customerUnitPackPrice = selectedPricing?.packSellPriceIncVat || 0;

    // Pharmacy price rule:
    // - VTCP/VTCU: pass-through (same as customer)
    // - else: purchase price
    const pharmacyUnitPackPrice = isDeliveryRoute
      ? customerUnitPackPrice
      : (selectedPricing?.packPurchasePriceIncVat || 0);

    customerTotal = packs * customerUnitPackPrice;
    pharmacyTotal = packs * pharmacyUnitPackPrice;
    
  } else {
    const inputValue = parseInt(unitsInput?.value || '0', 10);
    
    const route = getResolvedRoute().toLowerCase();
    const isDeliveryRoute = (route === 'vtcp' || route === 'vtcu');
    
    // Check if manual override is active
    if (manualOverrideActive && Object.keys(manualPackSelections).length > 0) {
      algorithmResult = calculateManualSelection(inputValue);
    } else if (allPackSizesAreOne) {
      // Direct pass-through: input is packs, not units
      algorithmResult = calculateDirectPacks(inputValue);
    } else if (packSizes.length === 1) {
      // Singular splittable product: simple division
      algorithmResult = calculateSingularProduct(inputValue);
    } else {
      // Full optimisation algorithm
      algorithmResult = calculateOptimalPacks(inputValue);
      // Pre-fill override inputs with recommendation
      prefillOverrideFromAlgorithm();
    }
    
    updateAlgorithmDisplay();
    updateOverrideVisibility();

    customerTotal = algorithmResult.customerRevenue || 0;
    pharmacyTotal = isDeliveryRoute ? customerTotal : (algorithmResult.purchaseCost || 0);
  }
  
  if (customerTotalEl) customerTotalEl.textContent = formatCurrency(customerTotal);
  if (pharmacyTotalEl) pharmacyTotalEl.textContent = formatCurrency(pharmacyTotal);
}


/**
 * Direct pack calculation for size=1 products (non-splittable)
 * Input IS the number of packs to order
 */
function calculateDirectPacks(packsNeeded) {
  if (packsNeeded <= 0 || packSizes.length === 0) {
    return createEmptyResult();
  }
  
  // For direct packs, we use the first (or only) pack size
  const pack = packSizes[0];
  
  // Only block if definitively out of stock, allow if unknown (null)
  if (pack.inStock === false) {
    return { ...createEmptyResult(), noStockAvailable: true };
  }
  // Flag if stock status unknown
  const stockUnknown = pack.inStock === null;
  
  const customerRevenue = roundMoney(packsNeeded * (pack.pricing?.packSellPriceIncVat || 0));
  const purchaseCost = roundMoney(packsNeeded * (pack.pricing?.packPurchasePriceIncVat || 0));
  
  return {
    packs: [{
      uom: pack.uom,
      size: pack.size,
      quantity: packsNeeded,
      packPurchasePrice: pack.pricing?.packPurchasePriceIncVat || 0,
      unitPurchasePrice: pack.pricing?.unitPurchasePriceIncVat || 0,
      unitSellPrice: pack.pricing?.unitSellPriceIncVat || 0
    }],
    allocation: [{
      uom: pack.uom,
      size: pack.size,
      quantity: packsNeeded,
      unitsAvailable: packsNeeded,  // Each pack = 1 unit
      unitsToCustomer: packsNeeded,
      unitsLeftover: 0,
      unitSellPrice: pack.pricing?.unitSellPriceIncVat || 0,
      unitPurchasePrice: pack.pricing?.unitPurchasePriceIncVat || 0,
      packPurchasePrice: pack.pricing?.packPurchasePriceIncVat || 0,
      packSellPrice: pack.pricing?.packSellPriceIncVat || 0,
      customerRevenue: customerRevenue
    }],
    totalUnits: packsNeeded,
    unitsNeeded: packsNeeded,
    wasteUnits: 0,
    purchaseCost: purchaseCost,
    customerRevenue: customerRevenue,
    remainderUnits: 0,
    remainderValue: 0,
    remainderFromPack: null,
    isDirectPack: true,  // Flag for display purposes
    stockUnknown: stockUnknown
  };
}

/**
 * Simple calculation for singular splittable product
 * ceil(units needed / pack size) = packs to order
 */
function calculateSingularProduct(unitsNeeded) {
  if (unitsNeeded <= 0 || packSizes.length === 0) {
    return createEmptyResult();
  }
  
  const pack = packSizes[0];
  
  // Only block if definitively out of stock, allow if unknown (null)
  if (pack.inStock === false) {
    return { ...createEmptyResult(), noStockAvailable: true };
  }
  // Flag if stock status unknown
  const stockUnknown = pack.inStock === null;
  
  const packSize = pack.size || 1;
  const packsToOrder = Math.ceil(unitsNeeded / packSize);
  const totalUnits = packsToOrder * packSize;
  const wasteUnits = totalUnits - unitsNeeded;
  
  const customerRevenue = roundMoney(unitsNeeded * (pack.pricing?.unitSellPriceIncVat || 0));
  const purchaseCost = roundMoney(packsToOrder * (pack.pricing?.packPurchasePriceIncVat || 0));
  const remainderValue = roundMoney(wasteUnits * (pack.pricing?.unitPurchasePriceIncVat || 0));
  
  return {
    packs: [{
      uom: pack.uom,
      size: packSize,
      quantity: packsToOrder,
      packPurchasePrice: pack.pricing?.packPurchasePriceIncVat || 0,
      unitPurchasePrice: pack.pricing?.unitPurchasePriceIncVat || 0,
      unitSellPrice: pack.pricing?.unitSellPriceIncVat || 0
    }],
    allocation: [{
      uom: pack.uom,
      size: packSize,
      quantity: packsToOrder,
      unitsAvailable: totalUnits,
      unitsToCustomer: unitsNeeded,
      unitsLeftover: wasteUnits,
      unitSellPrice: pack.pricing?.unitSellPriceIncVat || 0,
      unitPurchasePrice: pack.pricing?.unitPurchasePriceIncVat || 0,
      packPurchasePrice: pack.pricing?.packPurchasePriceIncVat || 0,
      customerRevenue: customerRevenue
    }],
    totalUnits: totalUnits,
    unitsNeeded: unitsNeeded,
    wasteUnits: wasteUnits,
    purchaseCost: purchaseCost,
    customerRevenue: customerRevenue,
    remainderUnits: wasteUnits,
    remainderValue: remainderValue,
    remainderFromPack: pack.uom,
    isSingularProduct: true,  // Flag for display purposes
    stockUnknown: stockUnknown
  };
}

/**
 * Calculate totals from manual pack selections
 */
function calculateManualSelection(unitsNeeded) {
  if (Object.keys(manualPackSelections).length === 0) {
    return createEmptyResult();
  }
  
  let totalUnits = 0;
  let purchaseCost = 0;
  const allocation = [];
  
  // First pass: calculate total units available
  for (const [uom, qty] of Object.entries(manualPackSelections)) {
    const pack = packSizes.find(p => p.uom === uom);
    if (!pack || qty <= 0) continue;
    totalUnits += qty * pack.size;
  }
  
  // Second pass: allocate units to customer, track remainder
  let unitsAllocated = 0;
  
  for (const [uom, qty] of Object.entries(manualPackSelections)) {
    const pack = packSizes.find(p => p.uom === uom);
    if (!pack || qty <= 0) continue;
    
    const unitsFromPack = qty * pack.size;
    const packPurchasePrice = pack.pricing?.packPurchasePriceIncVat || 0;
    const unitSellPrice = pack.pricing?.unitSellPriceIncVat || 0;
    const unitPurchasePrice = pack.pricing?.unitPurchasePriceIncVat || 0;
    
    // Calculate how many units from this pack go to customer
    const unitsStillNeeded = Math.max(0, unitsNeeded - unitsAllocated);
    const unitsToCustomer = Math.min(unitsFromPack, unitsStillNeeded);
    const unitsLeftover = unitsFromPack - unitsToCustomer;
    
    unitsAllocated += unitsToCustomer;
    purchaseCost += roundMoney(qty * packPurchasePrice);
    
    allocation.push({
      uom: pack.uom,
      size: pack.size,
      quantity: qty,
      unitsAvailable: unitsFromPack,
      unitsToCustomer: unitsToCustomer,
      unitsLeftover: unitsLeftover,
      unitSellPrice: unitSellPrice,
      unitPurchasePrice: unitPurchasePrice,
      packPurchasePrice: packPurchasePrice,
      customerRevenue: roundMoney(unitsToCustomer * unitSellPrice)
    });
  }
  
  const customerRevenue = roundMoney(allocation.reduce((sum, a) => sum + a.customerRevenue, 0));
  const wasteUnits = Math.max(0, totalUnits - unitsNeeded);
  const remainderValue = roundMoney(allocation.reduce((sum, a) => sum + (a.unitsLeftover * a.unitPurchasePrice), 0));
  
  return {
    packs: allocation.map(a => ({
      uom: a.uom,
      size: a.size,
      quantity: a.quantity,
      packPurchasePrice: a.packPurchasePrice,
      unitPurchasePrice: a.unitPurchasePrice,
      unitSellPrice: a.unitSellPrice
    })),
    allocation,
    totalUnits,
    unitsNeeded,
    wasteUnits,
    purchaseCost,
    customerRevenue,
    remainderUnits: wasteUnits,
    remainderValue,
    remainderFromPack: null,
    isManualOverride: true
  };
}

/**
 * Populate manual override inputs when pack sizes are loaded
 */
function populateOverrideInputs() {
  const container = document.getElementById('pack-override-inputs');
  const section = document.getElementById('pack-override-section');
  
  if (!container) return;
  
    // Never show override UI in Delivery mode
  if (fulfilmentMode === 'delivery') {
    if (section) section.style.display = 'none';
    container.innerHTML = '';
    return;
  }

  // Only show for grouped products with multiple splittable sizes
  if (packSizes.length <= 1 || allPackSizesAreOne) {
    if (section) section.style.display = 'none';
    container.innerHTML = '';
    return;
  }

  
  if (section) section.style.display = '';
  
  let html = `
    <div class="v4p-override-grid">
      <div class="v4p-override-grid__header">
        <span>Pack</span>
        <span>Stock</span>
        <span>Qty</span>
      </div>
  `;
  
  // Sort by size descending
  const sorted = [...packSizes].sort((a, b) => b.size - a.size);
  
  sorted.forEach(pack => {
    const packKey = pack.uom;
    const inStock = pack.inStock;
    // Only disable if definitively out of stock (false), allow if unknown (null)
    const disabled = inStock === false ? 'disabled' : '';
    const stockBadge = inStock === true 
      ? '<span class="v4p-tag v4p-tag--success v4p-tag--small">✓</span>'
      : inStock === false
        ? '<span class="v4p-tag v4p-tag--muted v4p-tag--small">OOS</span>'
        : '<span class="v4p-tag v4p-tag--warning v4p-tag--small">TBC</span>';
    
    html += `
      <div class="v4p-override-grid__row" data-uom="${escapeHtml(packKey)}">
        <span class="v4p-override-pack">${escapeHtml(packKey)}</span>
        <span class="v4p-override-stock">${stockBadge}</span>
        <input type="number" 
               class="v4p-form__input v4p-override-qty" 
               data-uom="${escapeHtml(packKey)}"
               value="0" 
               min="0" 
               step="1"
               ${disabled}>
      </div>
    `;
  });
  
  html += '</div>';
  container.innerHTML = html;
  
  // Bind input handlers
  container.querySelectorAll('.v4p-override-qty').forEach(input => {
    input.addEventListener('input', onManualOverrideChange);
    input.addEventListener('change', onManualOverrideChange);
  });
}

/**
 * Handle manual quantity input changes
 */
function onManualOverrideChange() {
  // Collect all manual selections
  manualPackSelections = {};
  let hasAnySelection = false;
  
  document.querySelectorAll('.v4p-override-qty').forEach(input => {
    const uom = input.dataset.uom;
    const qty = parseInt(input.value || '0', 10);
    if (qty > 0) {
      manualPackSelections[uom] = qty;
      hasAnySelection = true;
    }
  });
  
  manualOverrideActive = hasAnySelection;

  // Validate: total units must be >= units required
  const unitsInput = document.getElementById('calc-units-input');
  const unitsNeeded = parseInt(unitsInput?.value || '0', 10);
  
  let totalCustomUnits = 0;
  for (const [uom, qty] of Object.entries(manualPackSelections)) {
    const pack = packSizes.find(p => p.uom === uom);
    if (pack) totalCustomUnits += qty * pack.size;
  }
  
  // Update add button state based on validation
  const addBtn = document.getElementById('btn-add-line');
  if (addBtn && hasAnySelection && totalCustomUnits < unitsNeeded && unitsNeeded > 0) {
    const shortage = unitsNeeded - totalCustomUnits;
    addBtn.disabled = true;
    addBtn.textContent = `Need ${shortage} more units`;
  } else if (addBtn && selectedProductInStock) {
    addBtn.disabled = false;
    addBtn.textContent = 'Add to Order';
  }
  
  // Recalculate totals
  updateTotals();
  
  // Show/hide reset button
  const resetBtn = document.getElementById('btn-reset-override');
  if (resetBtn) {
    resetBtn.style.display = hasAnySelection ? '' : 'none';
  }
}


/**
 * Pre-fill override inputs with algorithm result
 */
function prefillOverrideFromAlgorithm() {
  if (!algorithmResult?.allocation) return;
  
  // Reset all to 0 first
  document.querySelectorAll('.v4p-override-qty').forEach(input => {
    input.value = '0';
  });
  
  // Fill in algorithm quantities
  algorithmResult.allocation.forEach(alloc => {
    const input = document.querySelector(`.v4p-override-qty[data-uom="${CSS.escape(alloc.uom)}"]`);
    if (input) {
      input.value = alloc.quantity;
    }
  });
  
}

/**
 * Reset to algorithm recommendation
 */
function resetToRecommended() {
  manualOverrideActive = false;
  manualPackSelections = {};
  
  // Reset all inputs to 0
  document.querySelectorAll('.v4p-override-qty').forEach(input => {
    input.value = '0';
  });
  
  // Recalculate with algorithm
  updateTotals();
  
  // Hide reset button
  const resetBtn = document.getElementById('btn-reset-override');
  if (resetBtn) resetBtn.style.display = 'none';
}

/**
 * Update override section visibility based on product type
 */
function updateOverrideVisibility() {
  const section = document.getElementById('pack-override-section');
  if (!section) return;
  
    // Never show override UI in Delivery mode
  if (fulfilmentMode === 'delivery') {
    section.style.display = 'none';
    return;
  }

  // Only show for grouped products with multiple splittable sizes
  if (packSizes.length <= 1 || allPackSizesAreOne) {
    section.style.display = 'none';
  } else {
    section.style.display = '';
  }
}

/**
 * Initialize override toggle button handlers
 */
function initOverrideToggle() {
  const toggleBtn = document.getElementById('btn-toggle-override');
  const resetBtn = document.getElementById('btn-reset-override');
  const inputsContainer = document.getElementById('pack-override-inputs');
  
  if (toggleBtn && inputsContainer) {
    toggleBtn.addEventListener('click', () => {
      const isExpanded = inputsContainer.style.display !== 'none';
      inputsContainer.style.display = isExpanded ? 'none' : '';
      toggleBtn.textContent = isExpanded ? '▶ Customise pack selection' : '▼ Customise pack selection';
    });
  }
  
  if (resetBtn) {
    resetBtn.addEventListener('click', resetToRecommended);
  }
}

/**
 * STAGE 1: Generate all viable pack combinations
 * STAGE 2: Score and select the best option
 */
function calculateOptimalPacks(unitsNeeded) {
  if (unitsNeeded <= 0 || packSizes.length === 0) {
    return createEmptyResult();
  }
  
  // Filter out definitively out-of-stock packs (allow null/unknown)
  const availablePacks = packSizes.filter(p => p.inStock !== false);
  
  if (availablePacks.length === 0) {
    return { ...createEmptyResult(), noStockAvailable: true };
  }
  
  // Flag if any pack has unknown stock status
  const stockUnknown = availablePacks.some(p => p.inStock === null);
  
  // Sort by size descending
  const sorted = [...availablePacks].sort((a, b) => b.size - a.size);
  
  // STAGE 1: Generate all candidate solutions
  const candidates = [];
  
  // Try greedy fill starting from each pack size
  for (let startIdx = 0; startIdx < sorted.length; startIdx++) {
    const packCombination = buildPackCombination(unitsNeeded, sorted, startIdx);
    
    if (packCombination && packCombination.packs.length > 0) {
      // Generate allocation options for this combination
      const allocationOptions = generateAllocationOptions(packCombination, unitsNeeded);
      candidates.push(...allocationOptions);
    }
  }
  
  if (candidates.length === 0) {
    return createEmptyResult();
  }
  
  // STAGE 2: Score and select best option
  const best = selectBestOption(candidates);
  
  // Add stockUnknown flag to result
  best.stockUnknown = stockUnknown;
  return best;
}

/**
 * Create empty result structure
 */
function createEmptyResult() {
  return {
    packs: [],
    allocation: [],
    totalUnits: 0,
    unitsNeeded: 0,
    wasteUnits: 0,
    purchaseCost: 0,
    customerRevenue: 0,
    remainderUnits: 0,
    remainderValue: 0,
    remainderFromPack: null
  };
}

/**
 * Build a pack combination using greedy fill from a starting index
 */
function buildPackCombination(unitsNeeded, sortedPacks, startIdx) {
  const packs = [];
  let remaining = unitsNeeded;
  
  // Greedy fill with floor counts
  for (let i = startIdx; i < sortedPacks.length && remaining > 0; i++) {
    const pack = sortedPacks[i];
    const count = Math.floor(remaining / pack.size);
    
    if (count > 0) {
      packs.push({
        uom: pack.uom,
        size: pack.size,
        quantity: count,
        packPurchasePrice: pack.pricing?.packPurchasePriceIncVat || 0,
        unitPurchasePrice: pack.pricing?.unitPurchasePriceIncVat || 0,
        unitSellPrice: pack.pricing?.unitSellPriceIncVat || 0
      });
      remaining -= count * pack.size;
    }
  }
  
  // Cover remainder with smallest pack that fits
  if (remaining > 0) {
    // Find smallest pack where size >= remaining (ascending order)
    const ascending = [...sortedPacks].reverse();
    const coveringPack = ascending.find(p => p.size >= remaining);
    
    if (coveringPack) {
      addOrIncrementPack(packs, {
        uom: coveringPack.uom,
        size: coveringPack.size,
        packPurchasePrice: coveringPack.pricing?.packPurchasePriceIncVat || 0,
        unitPurchasePrice: coveringPack.pricing?.unitPurchasePriceIncVat || 0,
        unitSellPrice: coveringPack.pricing?.unitSellPriceIncVat || 0
      });
    } else {
      // Fallback: use smallest pack, may need multiple
      const smallest = sortedPacks[sortedPacks.length - 1];
      const needed = Math.ceil(remaining / smallest.size);
      addOrIncrementPack(packs, {
        uom: smallest.uom,
        size: smallest.size,
        packPurchasePrice: smallest.pricing?.packPurchasePriceIncVat || 0,
        unitPurchasePrice: smallest.pricing?.unitPurchasePriceIncVat || 0,
        unitSellPrice: smallest.pricing?.unitSellPriceIncVat || 0
      }, needed);
    }
  }
  
  return { packs };
}

/**
 * Add pack to list or increment existing
 */
function addOrIncrementPack(packs, packData, count = 1) {
  const existing = packs.find(p => p.uom === packData.uom);
  if (existing) {
    existing.quantity += count;
  } else {
    packs.push({
      ...packData,
      quantity: count
    });
  }
}

/**
 * Generate all allocation options for a pack combination
 * If multiple pack types, remainder can come from any of them
 */
function generateAllocationOptions(packCombination, unitsNeeded) {
  const { packs } = packCombination;
  const options = [];
  
  const totalUnits = packs.reduce((sum, p) => sum + p.quantity * p.size, 0);
  const wasteUnits = totalUnits - unitsNeeded;
  const purchaseCost = roundMoney(packs.reduce((sum, p) => sum + p.quantity * p.packPurchasePrice, 0));
  
  if (wasteUnits === 0) {
    // No remainder - only one option
    const allocation = packs.map(pack => ({
      uom: pack.uom,
      size: pack.size,
      quantity: pack.quantity,
      unitsAvailable: pack.quantity * pack.size,
      unitsToCustomer: pack.quantity * pack.size,
      unitsLeftover: 0,
      unitSellPrice: pack.unitSellPrice,
      unitPurchasePrice: pack.unitPurchasePrice,
      packPurchasePrice: pack.packPurchasePrice,  // ADD THIS
      customerRevenue: roundMoney(pack.quantity * pack.size * pack.unitSellPrice)
    }));
    
    const customerRevenue = roundMoney(allocation.reduce((sum, a) => sum + a.customerRevenue, 0));
    
    options.push({
      packs: packs.map(p => ({ ...p })),
      allocation,
      totalUnits,
      unitsNeeded,
      wasteUnits: 0,
      purchaseCost,
      customerRevenue,
      remainderUnits: 0,
      remainderValue: 0,
      remainderFromPack: null
    });
    
  } else {
    // Has remainder - try remainder from each pack type
    for (const remainderPack of packs) {
      const option = createAllocationOption(packs, unitsNeeded, remainderPack.uom);
      if (option) {
        options.push(option);
      }
    }
  }
  
  return options;
}

/**
 * Create a specific allocation option where remainder comes from specified pack
 */
function createAllocationOption(packs, unitsNeeded, remainderFromUom) {
  const totalUnits = packs.reduce((sum, p) => sum + p.quantity * p.size, 0);
  const wasteUnits = totalUnits - unitsNeeded;
  const purchaseCost = roundMoney(packs.reduce((sum, p) => sum + p.quantity * p.packPurchasePrice, 0));
  
  // Find the pack remainder comes from
  const remainderPack = packs.find(p => p.uom === remainderFromUom);
  if (!remainderPack) return null;
  
  // Check if this pack has enough units to hold the remainder
  const remainderPackUnits = remainderPack.quantity * remainderPack.size;
  if (wasteUnits > remainderPackUnits) {
    // Can't take all remainder from this pack
    return null;
  }
  
  // Build allocation
  const allocation = packs.map(pack => {
    const unitsAvailable = pack.quantity * pack.size;
    let unitsToCustomer, unitsLeftover;
    
    if (pack.uom === remainderFromUom) {
      // Remainder comes from this pack
      unitsLeftover = wasteUnits;
      unitsToCustomer = unitsAvailable - wasteUnits;
    } else {
      // All units go to customer
      unitsLeftover = 0;
      unitsToCustomer = unitsAvailable;
    }
    
    return {
      uom: pack.uom,
      size: pack.size,
      quantity: pack.quantity,
      unitsAvailable,
      unitsToCustomer,
      unitsLeftover,
      unitSellPrice: pack.unitSellPrice,
      unitPurchasePrice: pack.unitPurchasePrice,
      packPurchasePrice: pack.packPurchasePrice,  // ADD THIS
      customerRevenue: roundMoney(unitsToCustomer * pack.unitSellPrice)
    };
  });
  
  const customerRevenue = roundMoney(allocation.reduce((sum, a) => sum + a.customerRevenue, 0));
  const remainderValue = roundMoney(wasteUnits * remainderPack.unitPurchasePrice);

  
  return {
    packs: packs.map(p => ({ ...p })),
    allocation,
    totalUnits,
    unitsNeeded,
    wasteUnits,
    purchaseCost,
    customerRevenue,
    remainderUnits: wasteUnits,
    remainderValue,
    remainderFromPack: remainderFromUom
  };
}

/**
 * STAGE 2: Select best option based on scoring priority
 * 
 * Priority:
 * 1. Min remainder value (£ at risk)
 * 2. Min purchase cost (pharmacy's cost)
 * 3. Min customer price (competitive)
 * 4. Min waste units (tiebreaker)
 */
function selectBestOption(candidates) {
  candidates.sort((a, b) => {
    // Priority 1: Min remainder value
    if (a.remainderValue !== b.remainderValue) {
      return a.remainderValue - b.remainderValue;
    }
    
    // Priority 2: Min purchase cost
    if (a.purchaseCost !== b.purchaseCost) {
      return a.purchaseCost - b.purchaseCost;
    }
    
    // Priority 3: Min customer price
    if (a.customerRevenue !== b.customerRevenue) {
      return a.customerRevenue - b.customerRevenue;
    }
    
    // Priority 4: Min waste units
    return a.wasteUnits - b.wasteUnits;
  });
  
  return candidates[0];
}

/**
 * Update the UI display with algorithm results
 * (Keeps original display format, uses new algorithm data)
 */
function updateAlgorithmDisplay() {
  const container = document.getElementById('algorithm-result');
  if (!container || !algorithmResult) return;

  // Check if no stock available FIRST
  if (algorithmResult.noStockAvailable) {
    container.innerHTML = '<p class="v4p-text-muted" style="margin:0; color: #dc3545;">⚠️ No pack sizes currently in stock</p>';
    return;
  }
  
  // Check if stock status unknown (server down)
  if (algorithmResult.stockUnknown) {
    container.innerHTML = '<p class="v4p-text-muted" style="margin:0; color: #f59e0b;">⚠️ Live stock status unavailable - order subject to confirmation</p>';
    // Don't return - continue to show algorithm results
  }

  if (!algorithmResult.allocation || algorithmResult.allocation.length === 0) {
    const labelText = allPackSizesAreOne ? 'packs' : 'units';
    container.innerHTML = `<p class="v4p-text-muted" style="margin:0;">Enter ${labelText} required above</p>`;
    return;
  }

  // Check if delivery route (VTCP or VTCU) - pharmacy cost = customer cost (pass-through)
  const route = getResolvedRoute().toLowerCase();
  const isDeliveryRoute = (route === 'vtcp' || route === 'vtcu');

  let html = '';

  algorithmResult.allocation.forEach((alloc) => {
    const qty = alloc.quantity || 0;
    const customerLineTotal = alloc.customerRevenue || 0;
    // For delivery routes: pharmacy pays same as customer (pass-through)
    const yourCostLineTotal = isDeliveryRoute ? customerLineTotal : (qty * (alloc.packPurchasePrice || 0));

    html += `
      <div class="v4p-algo-item">
        <div class="v4p-algo-item__title">
          <span class="v4p-algo-line__pk">${escapeHtml(selectedBaseName || '')} ${escapeHtml(alloc.uom || '')}</span>
        </div>

        <div class="v4p-algo-item__row">
          <span class="v4p-algo-k">Quantity:</span>
          <span class="v4p-algo-v">${qty}</span>

          <span class="v4p-algo-k">Customer:</span>
          <span class="v4p-algo-v">${formatCurrency(customerLineTotal)}</span>

          <span class="v4p-algo-k">Your cost:</span>
          <span class="v4p-algo-v v4p-algo-v--cost">${formatCurrency(yourCostLineTotal)}</span>
        </div>
      </div>
    `;
  });

  // For delivery routes: total pharmacy cost = total customer cost
  const totalYourCost = isDeliveryRoute ? algorithmResult.customerRevenue : algorithmResult.purchaseCost;

  // Adjust footer display based on calculation type
  let metricsHtml = '';
  
  // Show manual override indicator
  if (algorithmResult.isManualOverride) {
    metricsHtml += `<span class="v4p-tag v4p-tag--warning v4p-tag--small" style="margin-right: 8px;">Custom Selection</span>`;
  }
  
  if (algorithmResult.isDirectPack) {
    // Direct packs - no units/remainder concept
    metricsHtml += `<span>Packs to order: <strong>${algorithmResult.totalUnits}</strong></span>`;
  } else {
    // Units-based calculation
    metricsHtml += `
      <span>Units in packs: <strong>${algorithmResult.totalUnits}</strong></span>
      <span>Units remaining: <strong>${algorithmResult.wasteUnits}</strong></span>
    `;
  }

  html += `
    <div class="v4p-algo-footer">
      <div class="v4p-algo-metrics">
        ${metricsHtml}
      </div>

      <div class="v4p-algo-totals">
        <div class="v4p-algo-total-row"><span>Customer total</span><strong>${formatCurrency(algorithmResult.customerRevenue || 0)}</strong></div>
        <div class="v4p-algo-total-row"><span>Your cost total</span><strong>${formatCurrency(totalYourCost || 0)}</strong></div>
      </div>
    </div>
  `;

  container.innerHTML = html;
}
 // ============================================
// MODE HANDLERS
// ============================================
function setFulfilmentMode(mode) {
  if (mode !== 'collection' && mode !== 'delivery') return;

  // Delivery is disabled on New Order
  if (mode === 'delivery') {
    alert('Delivery is not available at the moment. Please select Collection.');
    // Re-highlight Collection button
    const fulfilmentGroup = document.getElementById('fulfilment-mode-group');
    if (fulfilmentGroup) {
      fulfilmentGroup.querySelectorAll('.v4p-btn-toggle').forEach(b => b.classList.remove('active'));
      const collBtn = fulfilmentGroup.querySelector('.v4p-btn-toggle[data-mode="collection"]');
      if (collBtn) collBtn.classList.add('active');
    }
    return;
  }

  fulfilmentMode = mode;

    // If switching to Delivery, kill manual override state/UI
  if (fulfilmentMode === 'delivery') {
    manualOverrideActive = false;
    manualPackSelections = {};

    const section = document.getElementById('pack-override-section');
    const inputs = document.getElementById('pack-override-inputs');
    const toggleBtn = document.getElementById('btn-toggle-override');
    const resetBtn = document.getElementById('btn-reset-override');

    if (section) section.style.display = 'none';
    if (inputs) { inputs.style.display = 'none'; inputs.innerHTML = ''; }
    if (toggleBtn) toggleBtn.textContent = '▶ Customise pack selection';
    if (resetBtn) resetBtn.style.display = 'none';
  }


  // Update toggle UI
  const fulfilmentGroup = document.getElementById('fulfilment-mode-group');
  if (fulfilmentGroup) {
    fulfilmentGroup.querySelectorAll('.v4p-btn-toggle').forEach(b => b.classList.remove('active'));
    const btn = fulfilmentGroup.querySelector(`.v4p-btn-toggle[data-mode="${mode}"]`);
    if (btn) btn.classList.add('active');
  }

  // Your existing behaviour on mode change
  clearSelection();
  updateRouteDisplay();
  updateModeDisplay();
}

  function initModeHandlers() {
    const fulfilmentGroup = document.getElementById('fulfilment-mode-group');
    const orderTypeGroup = document.getElementById('order-type-group');
    
    if (fulfilmentGroup) {
      fulfilmentGroup.addEventListener('click', (e) => {
        const btn = e.target.closest('.v4p-btn-toggle');
        if (!btn) return;
        
        fulfilmentGroup.querySelectorAll('.v4p-btn-toggle').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        setFulfilmentMode(btn.dataset.mode);
      });
    }
    
    if (orderTypeGroup) {
      orderTypeGroup.addEventListener('click', (e) => {
        const btn = e.target.closest('.v4p-btn-toggle');
        if (!btn) return;
        
        orderTypeGroup.querySelectorAll('.v4p-btn-toggle').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        orderType = btn.dataset.type;
        
        clearSelection();
        updateRouteDisplay();
        updateModeDisplay();
      });
    }
    
    updateRouteDisplay();
  }
  
  function clearSelection() {
    selectedProduct = null;
    selectedPricing = null;
    packSizes = [];
    selectedBaseName = null;
    algorithmResult = null;
    selectedProductInStock = null;
    isGroupedProduct = false;
    allPackSizesAreOne = false;
    manualOverrideActive = false;
    manualPackSelections = {};
    
    const searchInput = document.getElementById('product-search');
    const loadingEl = document.getElementById('product-loading');
    const contentEl = document.getElementById('product-content');
    const packsInput = document.getElementById('calc-packs-input');
    const unitsInput = document.getElementById('calc-units-input');
    
    if (searchInput) searchInput.value = '';
    // Clear old search suggestions so they never reappear
    const resultsContainer = document.getElementById('product-results');
    const listEl = document.getElementById('results-list');
    const countEl = document.getElementById('results-count');

    if (listEl) listEl.innerHTML = '';
    if (countEl) countEl.textContent = '0 products found';
    if (resultsContainer) resultsContainer.classList.remove('visible');

    if (packsInput) packsInput.value = '0';
    if (unitsInput) unitsInput.value = '0';
    if (loadingEl) {
      loadingEl.style.display = 'block';
     loadingEl.innerHTML = getEmptyStateHtml('Search for a product above');
    }
    if (contentEl) contentEl.style.display = 'none';
    
    const customerTotalEl = document.getElementById('calc-customer-total');
    const pharmacyTotalEl = document.getElementById('calc-pharmacy-total');
    if (customerTotalEl) customerTotalEl.textContent = '\u00A30.00';
    if (pharmacyTotalEl) pharmacyTotalEl.textContent = '\u00A30.00';
    
    // Reset add button
    const addBtn = document.getElementById('btn-add-line');
    if (addBtn) {
      addBtn.disabled = false;
      addBtn.textContent = 'Add to Order';
      addBtn.style.display = '';
    }

    // Reset override section
    const overrideSection = document.getElementById('pack-override-section');
    const overrideInputs = document.getElementById('pack-override-inputs');
    const toggleBtn = document.getElementById('btn-toggle-override');
    const resetBtn = document.getElementById('btn-reset-override');
    
    if (overrideSection) overrideSection.style.display = 'none';
    if (overrideInputs) {
      overrideInputs.style.display = 'none';
      overrideInputs.innerHTML = '';
    }
    if (toggleBtn) toggleBtn.textContent = '▶ Customise pack selection';
    if (resetBtn) resetBtn.style.display = 'none';
    
    // Reset repeat fields using snippet's function
    if (window.V4PResetRepeatFields) {
      window.V4PResetRepeatFields();
    }
  }
  
  // ============================================
  // PRODUCT INFO MODAL
  // ============================================
  function initProductInfoModal() {
    const btn = document.getElementById('btn-product-info');
    const modal = document.getElementById('product-info-modal');
    const closeBtn = document.getElementById('btn-close-product-modal');
    
    if (btn && modal) {
      btn.addEventListener('click', () => {
        updateProductInfoDisplay();
        modal.classList.add('visible');
      });
    }
    
    if (closeBtn && modal) {
      closeBtn.addEventListener('click', () => {
        modal.classList.remove('visible');
      });
    }
    
    // Close on backdrop click
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.classList.remove('visible');
        }
      });
    }
  }
  
  // ============================================
  // EXPORT
  // ============================================
  window.V4PCalculator = {
    getSelectedProduct: () => selectedProduct,
    getWorkerProduct: () => selectedProduct,  // Alias for compatibility
    getSelectedPricing: () => selectedPricing,
    getWorkerPricing: () => selectedPricing,  // Alias for compatibility
    getPackSizes: () => packSizes,
    getAlgorithmResult: () => algorithmResult,
    getResolvedRoute: getResolvedRoute,
    getFulfilmentMode: () => fulfilmentMode,
    getOrderType: () => orderType,
    isGroupedProduct: () => isGroupedProduct,
    isAllPackSizesOne: () => allPackSizesAreOne,
    isManualOverride: () => manualOverrideActive,
    getManualSelections: () => ({ ...manualPackSelections }),

    // Use the snippet's existing function
    getRepeatData: () => window.V4PGetRepeatData ? window.V4PGetRepeatData() : { repeatType: 'single' },
    getCanonicalUnitPrices: getCanonicalUnitPrices,

  };
  
  // ============================================
  // INIT
  // ============================================
  async function init() {
    console.log('V4P Calculator v5.2 (Manual Override): Initializing...');
    await checkWorkerHealth();
    initModeHandlers();
    initProductSearch();
    initCalculator();
    initProductInfoModal();
    initOverrideToggle();  // NEW: Initialize override handlers
    updateModeDisplay();
    console.log('V4P Calculator v5.2: Ready');
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();